// src/context/NewsContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import api from '../api';

const NewsContext = createContext();

const PAGE_LIMIT = 10;
const NEWS_FRESHNESS_MS = 60 * 1000;

const INITIAL_PAGINATION = {
  page: 1,
  limit: PAGE_LIMIT,
  total: 0,
  totalPages: 1,
  hasMore: false,
};

function getArticleIdentity(article) {
  return String(
    article?._id ||
    article?.canonicalUrl ||
    article?.sourceUrl ||
    article?.title ||
    ''
  );
}

function mergeUniqueArticles(
  firstArticles,
  remainingArticles
) {
  const seen = new Set();

  return [
    ...firstArticles,
    ...remainingArticles,
  ].filter((article) => {
    const identity =
      getArticleIdentity(article);

    if (
      !identity ||
      seen.has(identity)
    ) {
      return false;
    }

    seen.add(identity);
    return true;
  });
}

function getArticlesFromResponse(response) {
  return Array.isArray(
    response?.data?.articles
  )
    ? response.data.articles
    : [];
}

function getPaginationFromResponse(
  response,
  page,
  articleCount
) {
  const receivedPagination =
    response?.data?.pagination || {};

  return {
    page:
      Number(receivedPagination.page) ||
      page,

    limit:
      Number(receivedPagination.limit) ||
      PAGE_LIMIT,

    total:
      Number.isFinite(
        Number(receivedPagination.total)
      )
        ? Number(receivedPagination.total)
        : articleCount,

    totalPages:
      Number(receivedPagination.totalPages) ||
      1,

    hasMore:
      typeof receivedPagination.hasMore ===
      'boolean'
        ? receivedPagination.hasMore
        : false,
  };
}

export function NewsProvider({
  children,
}) {
  const [articles, setArticles] =
    useState([]);

  const [pagination, setPagination] =
    useState(INITIAL_PAGINATION);

  const [loading, setLoading] =
    useState(false);

  const [refreshing, setRefreshing] =
    useState(false);

  const [loadingMore, setLoadingMore] =
    useState(false);

  const [error, setError] =
    useState('');

  const [hasFetched, setHasFetched] =
    useState(false);

  const [lastFetchedAt, setLastFetchedAt] =
    useState(null);

  const latestStateRef = useRef({
    articles: [],
    pagination: INITIAL_PAGINATION,
    hasFetched: false,
    lastFetchedAt: null,
  });

  const latestRequestInFlightRef =
    useRef(false);

  const loadMoreInFlightRef =
    useRef(false);

  const updateArticles = useCallback(
    (nextValue) => {
      setArticles((previousArticles) => {
        const nextArticles =
          typeof nextValue === 'function'
            ? nextValue(previousArticles)
            : nextValue;

        latestStateRef.current.articles =
          nextArticles;

        return nextArticles;
      });
    },
    []
  );

  const updatePagination = useCallback(
    (nextValue) => {
      setPagination(
        (previousPagination) => {
          const nextPagination =
            typeof nextValue === 'function'
              ? nextValue(
                  previousPagination
                )
              : nextValue;

          latestStateRef.current.pagination =
            nextPagination;

          return nextPagination;
        }
      );
    },
    []
  );

  const fetchLatestNews = useCallback(
    async ({
      silent = false,
    } = {}) => {
      if (
        latestRequestInFlightRef.current
      ) {
        return;
      }

      latestRequestInFlightRef.current =
        true;

      const hasCachedArticles =
        latestStateRef.current.articles
          .length > 0;

      if (
        silent &&
        hasCachedArticles
      ) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      if (!silent) {
        setError('');
      }

      try {
        const response = await api.get(
          '/news',
          {
            params: {
              page: 1,
              limit: PAGE_LIMIT,
            },
          }
        );

        const nextArticles =
          getArticlesFromResponse(response);

        const nextPagination =
          getPaginationFromResponse(
            response,
            1,
            nextArticles.length
          );

        updateArticles(
          (previousArticles) => {
            if (
              !silent ||
              previousArticles.length === 0
            ) {
              return nextArticles;
            }

            return mergeUniqueArticles(
              nextArticles,
              previousArticles
            );
          }
        );

        updatePagination(
          (previousPagination) => {
            const currentArticles =
              silent
                ? mergeUniqueArticles(
                    nextArticles,
                    latestStateRef.current
                      .articles
                  )
                : nextArticles;

            const page =
              silent
                ? Math.max(
                    previousPagination.page,
                    1
                  )
                : 1;

            return {
              ...nextPagination,
              page,
              hasMore:
                nextPagination.total > 0
                  ? currentArticles.length <
                    nextPagination.total
                  : nextPagination.hasMore,
            };
          }
        );

        const fetchedAt = Date.now();

        latestStateRef.current.hasFetched =
          true;

        latestStateRef.current.lastFetchedAt =
          fetchedAt;

        setHasFetched(true);
        setLastFetchedAt(fetchedAt);
        setError('');
      } catch (requestError) {
        console.error(
          '❌ Failed to load News:',
          requestError
        );

        const message =
          requestError?.response?.data
            ?.message ||
          'News could not be loaded right now.';

        /*
         * A silent background failure must
         * not replace usable cached news.
         */
        if (!hasCachedArticles) {
          setError(message);
        }
      } finally {
        latestRequestInFlightRef.current =
          false;

        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      updateArticles,
      updatePagination,
    ]
  );

  const ensureNews = useCallback(
    async () => {
      const current =
        latestStateRef.current;

      if (!current.hasFetched) {
        await fetchLatestNews({
          silent: false,
        });

        return;
      }

      const cacheAge =
        Date.now() -
        Number(
          current.lastFetchedAt || 0
        );

      if (
        cacheAge >=
        NEWS_FRESHNESS_MS
      ) {
        await fetchLatestNews({
          silent: true,
        });
      }
    },
    [fetchLatestNews]
  );

  const refreshNews = useCallback(
    async () => {
      await fetchLatestNews({
        silent:
          latestStateRef.current.articles
            .length > 0,
      });
    },
    [fetchLatestNews]
  );

  const loadMoreNews = useCallback(
    async () => {
      const current =
        latestStateRef.current;

      if (
        loadMoreInFlightRef.current ||
        latestRequestInFlightRef.current ||
        !current.pagination.hasMore
      ) {
        return;
      }

      loadMoreInFlightRef.current = true;
      setLoadingMore(true);
      setError('');

      const nextPage =
        current.pagination.page + 1;

      try {
        const response = await api.get(
          '/news',
          {
            params: {
              page: nextPage,
              limit: PAGE_LIMIT,
            },
          }
        );

        const nextArticles =
          getArticlesFromResponse(response);

        const nextPagination =
          getPaginationFromResponse(
            response,
            nextPage,
            nextArticles.length
          );

        updateArticles(
          (previousArticles) =>
            mergeUniqueArticles(
              previousArticles,
              nextArticles
            )
        );

        updatePagination({
          ...nextPagination,
          page: nextPage,
        });

        setError('');
      } catch (requestError) {
        console.error(
          '❌ Failed to load more News:',
          requestError
        );

        setError(
          requestError?.response?.data
            ?.message ||
            'More news could not be loaded right now.'
        );
      } finally {
        loadMoreInFlightRef.current =
          false;

        setLoadingMore(false);
      }
    },
    [
      updateArticles,
      updatePagination,
    ]
  );

  const value = useMemo(
    () => ({
      articles,
      pagination,
      loading,
      refreshing,
      loadingMore,
      error,
      hasFetched,
      lastFetchedAt,
      ensureNews,
      refreshNews,
      loadMoreNews,
    }),
    [
      articles,
      pagination,
      loading,
      refreshing,
      loadingMore,
      error,
      hasFetched,
      lastFetchedAt,
      ensureNews,
      refreshNews,
      loadMoreNews,
    ]
  );

  return (
    <NewsContext.Provider value={value}>
      {children}
    </NewsContext.Provider>
  );
}

export function useNewsContext() {
  const context =
    useContext(NewsContext);

  if (!context) {
    throw new Error(
      'useNewsContext must be used within a NewsProvider'
    );
  }

  return context;
}
